import { INotificationMessage } from "@fl/shared/src/types/messages";
import {
  IDataExcel,
  IDataPDF,
  IFormFieldReport,
} from "@fl/shared/src/types/reports";
import { ISelectOptions } from "@fl/shared/src/types/select";
import { ITableDataColumns } from "@fl/shared/src/types/tables";
import React from "react";
import { Accordion, Button, Col, Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import ButtonGenerateReport from "../../components/generateReport";
import SelectComp from "../../components/select/selectComp";
import TableData from "../../components/tables";

type totalizerPositionType = "header" | "footer" | "mirror" | "none";

interface IProps {
  titleFilters?: string;
  titleResults?: string;
  fields: IFormFieldReport[];
  onRequestReport: (data: any) => void;
  resultsData?: any[];
  columnsTable: ITableDataColumns[];
  defaultValues: any;
  totalizerPosition?: totalizerPositionType;
  totalizerElement?: JSX.Element;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setMessagesExport?: (item: INotificationMessage[]) => void;
  getExportDataPDF: (data?: any) => IDataPDF;
  getExportDataExcel: (data?: any) => IDataExcel;
  exportButtonTitle?: string;
  reportName: string;
}

const DefaultReportPage: React.FC<IProps> = ({
  titleFilters = "Filtros",
  titleResults = "Resultados",
  fields,
  onRequestReport,
  resultsData = [],
  columnsTable,
  defaultValues = {},
  totalizerPosition = "none",
  totalizerElement = <></>,
  setLoading,
  setMessagesExport,
  getExportDataPDF,
  getExportDataExcel,
  exportButtonTitle,
  reportName,
}) => {
  const isDisabled = resultsData.length === 0 ? true : false;

  const methods = useForm({ defaultValues });
  const {
    register,
    reset,
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  const handleReset = () => {
    reset();
  };

  const onSubmit = (data: any) => {
    onRequestReport(data);
  };

  const renderField = (field: IFormFieldReport) => {
    const {
      fieldKey,
      label,
      type,
      placeholder,
      options,
      selectProps,
      validation,
      render,
      colSize,
    } = field;

    if (type === "custom" && render) {
      return (
        <Col key={fieldKey} xs={colSize || 12} className="mb-3">
          {label && <Form.Label>{label}:</Form.Label>}
          {render(methods, field)}
          {errors[fieldKey] && (
            <Form.Text className="text-danger">
              {errors[fieldKey]?.message as string}
            </Form.Text>
          )}
        </Col>
      );
    }

    switch (type) {
      case "select":
        return (
          <Col key={fieldKey} xs={colSize || 12} className="mb-3">
            {label && <Form.Label>{label}:</Form.Label>}
            <Controller
              name={fieldKey}
              control={control}
              rules={validation}
              render={({ field: { onChange, value } }) => {
                const isMulti = selectProps?.isMulti === true;

                // Para multi, `value` é um array de strings
                // Para single, `value` é uma string ou indefinida
                const selectedValue = isMulti
                  ? (options || []).filter((opt) =>
                      (value ?? []).includes(opt.value)
                    )
                  : (options || []).find((opt) => opt.value === value) || null;

                return (
                  <SelectComp
                    value={selectedValue}
                    options={options || []}
                    onChange={(selected: any) => {
                      if (isMulti) {
                        const values = selected
                          ? selected.map((s: ISelectOptions) => s.value)
                          : [];
                        onChange(values);
                      } else {
                        const val = selected ? selected.value : "";
                        onChange(val);
                      }
                    }}
                    placeholder={placeholder}
                    {...selectProps}
                  />
                );
              }}
            />
            {errors[fieldKey] && (
              <Form.Text className="text-danger">
                {errors[fieldKey]?.message as string}
              </Form.Text>
            )}
          </Col>
        );
      case "checkbox":
        return (
          <Col key={fieldKey} xs={colSize || 12} className="mb-3">
            <Form.Check
              type="checkbox"
              label={label}
              {...register(fieldKey, validation)}
              isInvalid={!!errors[fieldKey]}
            />
            {errors[fieldKey] && (
              <Form.Text className="text-danger">
                {errors[fieldKey]?.message as string}
              </Form.Text>
            )}
          </Col>
        );
      default:
        // Inputs padrão
        return (
          <Col key={fieldKey} xs={colSize || 12} className="mb-3">
            {label && <Form.Label>{label}:</Form.Label>}
            <Form.Control
              type={type}
              placeholder={placeholder}
              {...register(fieldKey, validation)}
              isInvalid={!!errors[fieldKey]}
            />
            <Form.Control.Feedback type="invalid">
              {errors[fieldKey]?.message as string}
            </Form.Control.Feedback>
          </Col>
        );
    }
  };

  return (
    <div>
      <Accordion defaultActiveKey="0" flush>
        <Accordion.Item eventKey={resultsData.length === 0 ? "0" : "1"}>
          <Accordion.Header>{titleFilters}</Accordion.Header>
          <Accordion.Body>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row>{fields.map((field) => renderField(field))}</Row>

              <div className="d-flex justify-content-between">
                <Button
                  variant="outline-danger"
                  type="button"
                  className="mt-4"
                  onClick={handleReset}
                >
                  Resetar Dados
                </Button>
                <Button
                  variant="outline-success"
                  type="submit"
                  className="mt-4"
                >
                  Gerar Relatório
                </Button>
              </div>
            </Form>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey={resultsData.length !== 0 ? "0" : "1"}>
          <Accordion.Header
            className={isDisabled ? "disabled-accordion-header" : ""}
          >
            {titleResults}
            {resultsData.length === 0 ? "" : ` [${resultsData.length}]`}
          </Accordion.Header>
          <Accordion.Body>
            {resultsData.length === 0 ? (
              <div>Nenhum resultado disponível</div>
            ) : (
              <div>
                {(totalizerPosition === "header" ||
                  totalizerPosition === "mirror") && <> {totalizerElement}</>}

                <TableData
                  columns={columnsTable}
                  data={resultsData}
                  showIndex
                  textSizeData={12}
                  textSizeHeader={14}
                />

                {(totalizerPosition === "footer" ||
                  totalizerPosition === "mirror") && <> {totalizerElement}</>}

                <div className="text-end mt-3">
                  <ButtonGenerateReport
                    exportButtonTitle={exportButtonTitle}
                    getExportDataExcel={getExportDataExcel}
                    getExportDataPDF={getExportDataPDF}
                    setLoading={setLoading}
                    setMessagesExport={setMessagesExport}
                    reportName={reportName}
                  />
                </div>
              </div>
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
};

export default DefaultReportPage;
